import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Ticket, CheckCircle2, XCircle, Loader2, Camera, Search,
  Clock, AlertTriangle, User, Phone, ArrowLeft, History
} from 'lucide-react';
import { API_URL, authApi } from '@/lib/api';

const statusStyles = {
  valid: 'bg-emerald-500/20 border-emerald-500/40 text-emerald-200',
  redeemed: 'bg-cyan-500/20 border-cyan-500/40 text-cyan-200',
  expired: 'bg-orange-500/20 border-orange-500/40 text-orange-200',
  invalid: 'bg-red-500/20 border-red-500/40 text-red-200',
  paused: 'bg-slate-700 text-slate-300',
  exhausted: 'bg-yellow-500/20 border-yellow-500/40 text-yellow-200',
};
const statusIcon = {
  valid: CheckCircle2, redeemed: CheckCircle2, expired: Clock,
  invalid: XCircle, paused: AlertTriangle, exhausted: AlertTriangle,
};
const inputCls = 'w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2.5 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/60 font-mono uppercase tracking-widest';

/**
 * Coupon redemption page for exhibitors.
 * - Manual code entry (or scan-QR — falls back to camera if supported)
 * - Live validation (calls POST /api/exhibitor/coupons/validate)
 * - Redeem CTA (POST /api/exhibitor/coupons/redeem)
 * - Redemption history table (GET /api/exhibitor/coupons/redemptions)
 */
export default function CouponRedemptionPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [customer, setCustomer] = useState({ name: '', phone: '' });
  const [status, setStatus] = useState(null);      // {status, message?, data?, remaining?}
  const [validating, setValidating] = useState(false);
  const [redeeming, setRedeeming] = useState(false);
  const [history, setHistory] = useState([]);
  const [flash, setFlash] = useState(null);
  const notify = (msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 4000);
  };
  const authHeaders = { Authorization: `Bearer ${authApi.getToken()}`, Accept: 'application/json' };

  useEffect(() => {
    if (!authApi.isAuthenticated() || !authApi.isExhibitor()) {
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/exhibitor/coupons/redemptions`, { headers: authHeaders });
      setHistory(r.data.data || []);
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const validate = async () => {
    const c = code.trim().toUpperCase();
    if (!c) { notify('Enter a coupon code first', true); return; }
    setValidating(true);
    setStatus(null);
    try {
      const r = await axios.post(`${API_URL}/exhibitor/coupons/validate`, { coupon_code: c }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setStatus(r.data);
    } catch (e) {
      setStatus({ status: 'invalid', message: e.response?.data?.message || 'Validation failed' });
    } finally {
      setValidating(false);
    }
  };

  const redeem = async () => {
    if (status?.status !== 'valid') return;
    setRedeeming(true);
    try {
      const payload = {
        coupon_code: code.trim().toUpperCase(),
        customer_name: customer.name || undefined,
        customer_phone: customer.phone || undefined,
      };
      const r = await axios.post(`${API_URL}/exhibitor/coupons/redeem`, payload, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      notify('✓ Coupon redeemed');
      setStatus({ status: 'redeemed', data: r.data.data.announcement });
      setCode('');
      setCustomer({ name: '', phone: '' });
      loadHistory();
    } catch (e) {
      const s = e.response?.data?.status || 'invalid';
      notify(e.response?.data?.message || 'Redeem failed', true);
      setStatus({ status: s, message: e.response?.data?.message });
    } finally {
      setRedeeming(false);
    }
  };

  const scanQR = async () => {
    try {
      // Modern browsers: use the BarcodeDetector API if available.
      // eslint-disable-next-line no-undef
      if ('BarcodeDetector' in window) {
        const detector = new BarcodeDetector({ formats: ['qr_code'] });
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        const video = document.createElement('video');
        video.srcObject = stream;
        await video.play();
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,.85);display:flex;flex-direction:column;align-items:center;justify-content:center;padding:20px;';
        overlay.appendChild(video);
        const cancel = document.createElement('button');
        cancel.textContent = 'Cancel';
        cancel.style.cssText = 'margin-top:16px;padding:10px 24px;background:#ef4444;color:white;border:0;border-radius:9999px;font-weight:800;';
        overlay.appendChild(cancel);
        document.body.appendChild(overlay);
        let stopped = false;
        const stop = () => {
          if (stopped) return;
          stopped = true;
          stream.getTracks().forEach((t) => t.stop());
          overlay.remove();
        };
        cancel.onclick = stop;
        const tick = async () => {
          if (stopped) return;
          try {
            const codes = await detector.detect(video);
            if (codes && codes.length > 0) {
              const raw = codes[0].rawValue;
              // If it's a URL like /redeem/ABC123 pull the code
              const m = raw.match(/redeem\/(\S+)$/);
              const parsed = (m ? m[1] : raw).trim().toUpperCase();
              setCode(parsed);
              stop();
              // Auto-validate
              setTimeout(() => validate(), 100);
              return;
            }
          } catch { /* ignore per-tick */ }
          requestAnimationFrame(tick);
        };
        tick();
      } else {
        notify('QR scanning is not supported on this browser — enter code manually.', true);
      }
    } catch (e) {
      notify('Camera permission denied', true);
    }
  };

  const StatusIcon = status ? (statusIcon[status.status] || AlertTriangle) : null;

  return (
    <div className="min-h-screen bg-slate-950 text-white pb-16">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900/80 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate('/exhibitor')} className="p-2 rounded-lg hover:bg-slate-800 text-slate-300" data-testid="back-to-dashboard">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 grid place-items-center">
            <Ticket className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-emerald-400 text-[10px] uppercase tracking-widest font-bold">Coupon Redemption</div>
            <div className="text-white font-bold text-sm">Verify & redeem visitor coupons</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {flash && (
          <div className={`px-3 py-2 rounded-lg text-sm font-semibold ${flash.isError ? 'bg-red-500/20 text-red-200 border border-red-500/40' : 'bg-emerald-500/20 text-emerald-200 border border-emerald-500/40'}`} data-testid="redeem-flash">
            {flash.msg}
          </div>
        )}

        {/* Code entry card */}
        <div className="bg-slate-900/60 rounded-2xl p-5 border border-slate-800">
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-2">Coupon code</div>
          <div className="flex gap-2 flex-wrap">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') validate(); }}
              placeholder="ABC12345"
              className={`${inputCls} flex-1 min-w-[160px] text-lg`}
              data-testid="coupon-code-input"
              autoFocus
            />
            <button
              onClick={validate}
              disabled={validating || !code.trim()}
              className="px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-50"
              data-testid="validate-btn"
            >
              {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Verify
            </button>
            <button
              onClick={scanQR}
              className="px-4 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold flex items-center gap-1.5"
              data-testid="scan-qr-btn"
              title="Scan QR (camera)"
            >
              <Camera className="w-4 h-4" /> Scan
            </button>
          </div>

          {/* Status card */}
          {status && (
            <div className={`mt-4 p-4 rounded-xl border ${statusStyles[status.status] || statusStyles.invalid}`} data-testid="coupon-status">
              <div className="flex items-center gap-2 mb-2">
                {StatusIcon && <StatusIcon className="w-5 h-5" />}
                <div className="font-black text-sm uppercase tracking-widest">{status.status}</div>
                {status.remaining != null && <div className="ml-auto text-[10px]">{status.remaining} left</div>}
              </div>
              {status.data && (
                <>
                  <div className="text-white font-bold text-lg">{status.data.title}</div>
                  {status.data.discount_label && <div className="text-yellow-300 font-black text-sm">{status.data.discount_label}</div>}
                  {status.data.body && <div className="text-xs mt-1 opacity-80">{status.data.body}</div>}
                  {status.data.expires_at && <div className="text-[10px] opacity-70 mt-1">Expires: {new Date(status.data.expires_at).toLocaleString()}</div>}
                </>
              )}
              {status.message && <div className="text-xs mt-2 opacity-90">{status.message}</div>}

              {status.status === 'valid' && (
                <div className="mt-4 grid sm:grid-cols-2 gap-2">
                  <div className="relative">
                    <User className="w-3.5 h-3.5 absolute left-2.5 top-2.5 opacity-60" />
                    <input value={customer.name} onChange={(e) => setCustomer((p) => ({ ...p, name: e.target.value }))} placeholder="Customer name (optional)" className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-3 py-2 text-white placeholder-slate-500 text-xs" data-testid="customer-name-input" />
                  </div>
                  <div className="relative">
                    <Phone className="w-3.5 h-3.5 absolute left-2.5 top-2.5 opacity-60" />
                    <input value={customer.phone} onChange={(e) => setCustomer((p) => ({ ...p, phone: e.target.value }))} placeholder="Customer phone (optional)" className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-8 pr-3 py-2 text-white placeholder-slate-500 text-xs" data-testid="customer-phone-input" />
                  </div>
                  <button
                    onClick={redeem}
                    disabled={redeeming}
                    className="sm:col-span-2 mt-1 px-4 py-2.5 rounded-lg bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="redeem-btn"
                  >
                    {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Redeem Coupon
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* History */}
        <div className="bg-slate-900/60 rounded-2xl p-5 border border-slate-800">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-cyan-400" />
            <div className="text-cyan-400 text-[10px] uppercase tracking-widest font-bold">Redemption History</div>
            <div className="ml-auto text-[10px] text-slate-500">{history.length} total</div>
          </div>
          {history.length === 0 ? (
            <div className="text-slate-500 text-xs italic">No redemptions yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="redemption-history">
                <thead>
                  <tr className="text-left text-slate-500 uppercase text-[10px] border-b border-slate-800">
                    <th className="py-2">Time</th>
                    <th>Code</th>
                    <th>Coupon</th>
                    <th>Customer</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/50 text-slate-300">
                      <td className="py-2 whitespace-nowrap">{new Date(r.redeemed_at).toLocaleString()}</td>
                      <td className="font-mono text-cyan-300">{r.coupon_code}</td>
                      <td className="truncate max-w-[200px]">{r.announcement?.title || '—'}<div className="text-yellow-300 text-[10px]">{r.announcement?.discount_label}</div></td>
                      <td>{r.customer_name || '—'}{r.customer_phone && <div className="text-[10px] text-slate-500">{r.customer_phone}</div>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
